import { useContext, useEffect, useState } from "react";
import { Box, Button, Flex, Input, Spinner, VStack, Text } from "@chakra-ui/react";
import _ from "underscore";
import { apiRequest } from "utils/api";
import { AlertsContext } from "providers/alerts";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import NoResults from "components/elements/NoResults";
import { ChecklistContext } from "providers/checklist";
import CategoryList from "./CategoryList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import WidgetBox from "components/elements/WidgetBox/widget-box";
import { WidgetBoxActions } from "components/elements/WidgetBox/widget-box-actions";
import InputSearch from "components/elements/InputSearch/input-search";

interface IChecklistItemsProps {
  canAdd?: boolean;
  canEdit?: boolean;
  canView?: boolean;
}

const ChecklistItems = ({
  canAdd = false,
  canEdit = false,
  canView = false,
}: IChecklistItemsProps) => {
  const { user } = useTenant();
  const { showSuccess, showError } = useContext(AlertsContext);

  const { items, reorder, setReorder, isLoading } = useContext(ChecklistContext);

  const [filter, setFilter] = useState("");
  const [list, setList] = useState<any[]>([]);

  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [showAddCategoryInput, setShowAddCategoryInput] = useState(false);

  // const checkSelectAllToggle = useCallback(() => {
  //     const categoriesWithAllCompletedItems = list.filter(
  //         (item) =>
  //             item.items.length > 0 &&
  //             item.items.every((subItem: any) => subItem.completed === true)
  //     );

  //     categoriesWithAllCompletedItems.forEach(
  //         (category) => category.category
  //     );
  // }, [list]);

  // useEffect(() => {
  //     if (list) {
  //         checkSelectAllToggle();
  //     }
  // }, [checkSelectAllToggle, list]);

  const saveOrder = async () => {
    try {
      setSaving(true);
      await apiRequest("/supabase/loan_items/upsert", {
        items: list
          .map(item => item.items)
          .flat()
          .map((item, index) => ({
            id: item.id,
            order: index + 1,
          })),
      });
      showSuccess({
        title: "Order Saved",
      });
      setReorder(false);
    } catch (err) {
      showError();
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!items || items.length === 0) {
      return;
    }

    if (!user || !user.role || !user.role.id) {
      return;
    }

    const uniqueItems = new Map();

    items.forEach((item: any) => {
      if (!uniqueItems.has(item.id) && item.title && item.title.trim() !== "") {
        uniqueItems.set(item.id, item);
      }
    });

    const uniqueItemsArray = Array.from(uniqueItems.values());

    // Apply search filter if there's a search term
    const filteredItems = filter
      ? uniqueItemsArray.filter(
          (item: any) =>
            item.title.toLowerCase().includes(filter.toLowerCase()) ||
            (item.category && item.category.toLowerCase().includes(filter.toLowerCase()))
        )
      : uniqueItemsArray;

    const categoryMap = _.groupBy(filteredItems, (item: any) => item.category || "Uncategorized");

    const newList = Object.keys(categoryMap)
      .map(category => {
        const categoryItems = categoryMap[category];

        const filteredItems = categoryItems.filter((item: any) => {
          if (!item.title || item.title.trim() === "") {
            return false;
          }

          const hasDirectAssignments =
            item.assignments && Array.isArray(item.assignments) && item.assignments.length > 0;

          const hasChecklistItemAssignments =
            item.checklist_item &&
            item.checklist_item.assignments &&
            Array.isArray(item.checklist_item.assignments) &&
            item.checklist_item.assignments.length > 0;

          if (!hasDirectAssignments && !hasChecklistItemAssignments) {
            return true;
          }

          if (hasDirectAssignments) {
            const isDirectlyAssigned = item.assignments.includes(user.role.id);
            if (isDirectlyAssigned) {
              return true;
            }
          }

          if (hasChecklistItemAssignments) {
            const isAssignedViaChecklistItem = item.checklist_item.assignments.includes(
              user.role.id
            );
            return isAssignedViaChecklistItem;
          }

          return false;
        });

        if (filteredItems.length === 0) {
          return null;
        }

        return {
          category,
          items: filteredItems,
        };
      })
      .filter(group => group !== null)
      .filter(group => group!.items.length > 0);

    setList(newList);
  }, [items, filter, user]);

  const addNewCategory = async () => {
    if (newCategory.trim() !== "") {
      try {
        setList(prevList => [
          ...prevList,
          {
            category: newCategory,
            items: [],
          },
        ]);

        showSuccess({
          title: "Category Added",
        });

        setNewCategory("");
        setShowAddCategoryInput(false);
      } catch (err) {
        showError();
      }
    }
  };

  const showButtons = () => {
    setShowAddCategoryInput(true);
  };

  if (isLoading) {
    return (
      <Box>
        <Spinner />
      </Box>
    );
  }
  if (!items?.length || !canView) {
    return <NoResults message={`You haven't been assigned any checklist items yet.`} />;
  }

  return (
    <WidgetBox title="Checklist">
      <VStack align="stretch" spacing={4}>
        <Text fontSize="13px" color="gray.600" mt="20px">
          Use this checklist to keep the loan progress on track. Add people, upload documents, and
          add comments to checklist items.
        </Text>
        <Flex gap={2}>
          <InputSearch
            value={filter}
            onChange={ev => setFilter(ev.target.value)}
            flex={1}
            size="sm"
            h="35px"
          />
          {user.type === "lender" && !reorder && (
            <Button
              onClick={() => setReorder(true)}
              isDisabled={!canEdit}
              px="12px"
              h="35px"
              size="sm"
              w="180px"
              variant="secondary"
              ml="18px"
            >
              Re-Order
            </Button>
          )}
        </Flex>
        <WidgetBoxActions>
          {user.type === "lender" && reorder && (
            <Flex gap={2}>
              <Button
                variant="secondary"
                onClick={() => setReorder(false)}
                px="12px"
                h="35px"
                size="sm"
                w="180px"
              >
                Cancel
              </Button>
              <Button
                onClick={saveOrder}
                isLoading={saving}
                isDisabled={!canEdit}
                px="12px"
                h="35px"
                size="sm"
                w="180px"
                variant="secondary"
              >
                Save Order
              </Button>
            </Flex>
          )}
        </WidgetBoxActions>
        <Flex flexDirection="column" gap={2}>
          <Box>
            <Button
              variant="ghost"
              color="purple.600"
              size="sm"
              pl="0px"
              mt="10px"
              mb="20px"
              onClick={showButtons}
              leftIcon={<FontAwesomeIcon icon={["far", "plus"]} />}
              isDisabled={!canEdit}
            >
              New Category
            </Button>
          </Box>

          {user.type === "lender" && showAddCategoryInput && (
            <Flex gap={2}>
              <Input
                variant="primary"
                placeholder="New Category..."
                size="sm"
                h="35px"
                w="400px"
                value={newCategory}
                onChange={ev => setNewCategory(ev.target.value)}
              />
              <Button
                onClick={addNewCategory}
                variant="secondary"
                px="12px"
                h="35px"
                size="sm"
                w="80px"
                ml="18px"
              >
                Add
              </Button>
              <Button
                onClick={() => {
                  setNewCategory("");
                  setShowAddCategoryInput(false);
                }}
                variant="ghost"
                px="12px"
                h="35px"
                size="sm"
                w="80px"
              >
                Cancel
              </Button>
            </Flex>
          )}
          <Box>
            <CategoryList
              filter={filter}
              list={list}
              setList={setList}
              canEdit={canEdit}
              canAdd={canAdd}
            />
          </Box>
        </Flex>
      </VStack>
    </WidgetBox>
  );
};

export default ChecklistItems;
